import { setUser } from '@sentry/react'
import { useEffect, useLayoutEffect, useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import clsx from 'clsx'

import { User01, ShoppingCart01, Menu05, XClose } from '@untitledui/icons'

import BalticLogo from '@/components/BalticLogo'
import AccountDrawer from '@/components/AccountDrawer'
import useTranslation from '@/hooks/useTranslation'
import useCart from '@/hooks/useCart'
import useLockBodyScroll from '@/hooks/useLockBodyScroll'
import useVersionCheck from '@/hooks/useVersionCheck'
import useEscapeKey from '@/hooks/useEscapeKey'
import useRybbit from '@/hooks/useRybbit'

const ICONS = {
    cart: ShoppingCart01,
    user: User01,
}

function NavItem({ to, label, icon, onClick, badge }) {
    const { pathname } = useLocation()
    const isActive = to
        ? to === '/'
            ? pathname === '/'
            : pathname.startsWith(to)
        : false
    const Icon = icon && ICONS[icon]

    const className = clsx(
        'relative flex items-center justify-center text-center font-semibold h-[40px] rounded-[8px] cursor-pointer',
        Icon
            ? 'w-[40px]'
            : 'min-w-[120px] text-[13px] tracking-wider px-[24px]',
        isActive && 'text-dark-blue',
        !isActive && 'hover:bg-white/50'
    )

    const content = (
        <>
            {isActive && (
                <motion.div
                    layoutId="nav-active"
                    className="absolute inset-0 bg-white rounded-[8px]"
                    style={{ originY: '0px' }}
                    transition={{ type: 'spring', bounce: 0.15, duration: 0.3 }}
                />
            )}
            <span className="relative z-10">
                {Icon ? <Icon size={20} strokeWidth={1.7} /> : label}
            </span>
            {badge > 0 && (
                <span className="absolute -top-[4px] -right-[4px] z-20 min-w-[18px] h-[18px] rounded-full bg-dark-blue text-white text-[10px] font-semibold flex items-center justify-center px-[4px]">
                    {badge}
                </span>
            )}
        </>
    )

    if (to) {
        return (
            <Link to={to} className={className} onClick={onClick}>
                {content}
            </Link>
        )
    }

    return (
        <button onClick={onClick} className={className}>
            {content}
        </button>
    )
}

function useScrollToTopOnNavigate() {
    const { pathname } = useLocation()

    useLayoutEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' })
    }, [pathname])
}

function MobileMenu({ isOpen, onClose, navItems }) {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    useLockBodyScroll(isOpen)
    useEscapeKey(isOpen, onClose)

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
                    className="fixed inset-0 top-[68px] z-10 bg-white flex flex-col lg:hidden"
                >
                    <div className="flex flex-col flex-1 p-[24px] gap-[4px]">
                        {navItems.map((item) => {
                            const isActive = item.to
                                ? item.to === '/'
                                    ? pathname === '/'
                                    : pathname.startsWith(item.to)
                                : false
                            return (
                                <button
                                    key={item.to || item.icon}
                                    onClick={() => {
                                        if (item.to) navigate(item.to)
                                        item.onClick?.()
                                        onClose()
                                    }}
                                    className={clsx(
                                        'text-left text-[18px] font-semibold uppercase px-[16px] py-[12px] rounded-[8px] cursor-pointer flex items-center gap-[8px]',
                                        isActive
                                            ? 'bg-dark-blue/4 text-dark-blue'
                                            : 'text-dark-blue-40'
                                    )}
                                >
                                    {item.label}
                                    {item.badge > 0 && (
                                        <span className="min-w-[22px] h-[22px] rounded-full bg-dark-blue text-white text-[11px] font-semibold flex items-center justify-center px-[5px]">
                                            {item.badge}
                                        </span>
                                    )}
                                </button>
                            )
                        })}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

function Layout({ onLogout, retailer }) {
    const { t } = useTranslation()
    const { cartCount } = useCart()
    const [isAccountOpen, setIsAccountOpen] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    useScrollToTopOnNavigate()
    useVersionCheck()
    useRybbit(retailer)

    useEffect(() => {
        setUser(
            retailer?.public_id
                ? {
                      id: retailer.public_id,
                      ...(retailer.name ? { username: retailer.name } : {}),
                  }
                : null
        )
        return () => setUser(null)
    }, [retailer?.public_id, retailer?.name])

    const isAdmin = retailer?.access_level === 'admin'

    const navItems = [
        { to: '/', label: t('nav.products') },
        ...(isAdmin ? [{ to: '/orders', label: t('nav.orders') }] : []),
        { to: '/warranty', label: t('nav.warranty') },
        {
            label: t('account.title'),
            icon: 'user',
            onClick: () => setIsAccountOpen(true),
        },
        ...(isAdmin
            ? [
                  {
                      to: '/cart',
                      label: t('nav.cart'),
                      icon: 'cart',
                      badge: cartCount,
                  },
              ]
            : []),
    ]

    return (
        <div className="min-h-screen-safe bg-white">
            <div className="fixed top-0 left-0 right-0 z-10 pt-[28px] hidden lg:flex">
                <header className="flex items-center p-[4px] gap-[4px] relative mx-auto bg-dark-blue/4 rounded-[12px] text-dark-blue backdrop-blur-[100px]">
                    <Link
                        to="/"
                        className="relative h-[40px] min-w-[120px] flex items-center justify-center"
                    >
                        <BalticLogo size={12} strokeWidth={12} />
                    </Link>

                    {navItems.map((item) => (
                        <NavItem
                            key={item.to || item.icon}
                            to={item.to}
                            label={item.label}
                            icon={item.icon}
                            onClick={item.onClick}
                            badge={item.badge}
                        />
                    ))}
                </header>
            </div>

            <div className="fixed left-0 right-0 top-0 bg-white z-10 flex items-center justify-between lg:hidden w-full h-[68px] border-b border-dark-blue-10 px-[24px] py-[12px]">
                <Link to="/" className="relative h-[40px] flex items-center">
                    <BalticLogo size={12} strokeWidth={12} />
                </Link>

                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="cursor-pointer"
                >
                    {isMobileMenuOpen ? (
                        <XClose size={24} />
                    ) : (
                        <Menu05 size={24} />
                    )}
                </button>
            </div>

            <MobileMenu
                isOpen={isMobileMenuOpen}
                onClose={() => setIsMobileMenuOpen(false)}
                navItems={navItems}
            />

            <main className="relative">
                <Outlet />
            </main>

            <AccountDrawer
                isOpen={isAccountOpen}
                onClose={() => setIsAccountOpen(false)}
                onLogout={onLogout}
            />

            <div id="vaul-portal" className="z-50 relative" />
        </div>
    )
}

export default Layout
